import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { useDocumentHead } from '@/hooks/use-document-head';
import { PricingCalculator } from './components/pricing-calculator';

const RESIDENTIAL_TIERS = [
  {
    title: 'STANDARD CLEAN',
    price: '$150 – $350',
    tag: 'MOST POPULAR',
    features: [
      'Kitchen & bathroom cleaning',
      'Vacuuming & mopping floors',
      'Dusting all surfaces',
      'Trash removal',
      'Surface sanitization',
    ],
    highlight: false,
  },
  {
    title: 'DEEP CLEAN',
    price: '$250 – $550',
    tag: 'FULL RESET',
    features: [
      'Everything in Standard +',
      'Inside oven & refrigerator',
      'Baseboards & ceiling fans',
      'Window sills & tracks',
      'Behind & under furniture',
      'Cabinet fronts & hardware',
    ],
    highlight: true,
  },
  {
    title: 'MOVE-IN/OUT',
    price: '$275 – $650',
    tag: 'HANDOVER',
    features: [
      'Everything in Deep Clean +',
      'Inside all cabinets & drawers',
      'Appliance interior detail',
      'Light fixture cleaning',
      'Full window cleaning',
      'Garage sweep (if applicable)',
    ],
    highlight: false,
  },
];

const COMMERCIAL_RATES = [
  { type: 'Office Space', rate: '$0.09 – $0.17 / sq ft', note: 'Daily, weekly, or custom' },
  { type: 'Medical / Clinical', rate: '$0.14 – $0.29 / sq ft', note: 'EPA-registered disinfectants' },
  { type: 'Post-Construction', rate: 'Custom quote', note: 'Based on debris level & scope' },
  { type: 'Retail / Hospitality', rate: '$0.10 – $0.20 / sq ft', note: 'After-hours availability' },
];

export default function PricingPage() {
  useDocumentHead(
    'Cleaning Prices in Orlando, FL | Sonz of Thunder',
    'See cleaning service pricing for Orlando & Central Florida from Sonz of Thunder Services (SOTSVC). Get a free custom quote.',
  );
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] clip-diag-bottom overflow-hidden">
        <img src="/images/pages/pricing-hero.webp" alt="" className="absolute inset-0 w-full h-full object-cover ken-burns" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(15,15,30,.92) 0%, rgba(15,15,30,.6) 60%, rgba(0,180,216,.2) 100%)' }} />
        <div className="absolute inset-0 opacity-20 grid-pattern" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full fade-in">
            <div className="flex items-center gap-4 mb-6">
              <span className="h-[3px] w-16 bg-thunder" />
              <span className="text-thunder font-display tracking-[0.4em] text-xs">// TRANSPARENT PRICING</span>
            </div>
            <h1 className="font-display font-bold text-white text-4xl sm:text-6xl md:text-8xl leading-[0.88]">
              PRICING <span className="text-thunder">THAT HITS</span>
            </h1>
            <p className="text-white/80 text-lg sm:text-xl max-w-xl mt-6 font-light">
              No hidden fees. No surprise charges. Every quote is custom, but here's where we start.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <PricingCalculator />

      {/* Residential Tiers */}
      <section className="relative py-28 bg-charcoal-deep overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <ScrollReveal className="text-center mb-16">
            <div className="text-electric font-display tracking-[0.4em] text-xs mb-3">// RESIDENTIAL</div>
            <h2 className="font-display font-bold text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
              HOME <span className="text-thunder">CLEANING</span>
            </h2>
            <p className="text-white/60 text-lg mt-6 max-w-xl mx-auto">
              Pricing varies by home size, condition, and scope. These ranges cover most Central Florida homes.
            </p>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-6">
            {RESIDENTIAL_TIERS.map((tier, i) => (
              <ScrollReveal key={tier.title} delay={i * 100}>
                <div
                  className={`relative h-full flex flex-col ${
                    tier.highlight
                      ? 'bg-charcoal-mid border-2 border-thunder glow-thunder'
                      : 'bg-charcoal-mid/40 border border-electric/20'
                  } p-8 lg:p-10`}
                >
                  {tier.highlight && (
                    <div className="absolute -top-4 left-8 bg-thunder text-charcoal-deep font-display font-bold px-4 py-1 tracking-[0.2em] text-xs">
                      RECOMMENDED
                    </div>
                  )}
                  <div className="font-display text-electric tracking-[0.3em] text-xs mb-3">{tier.tag}</div>
                  <h3 className="font-display font-bold text-white text-3xl mb-2 tracking-wider">{tier.title}</h3>
                  <div
                    className={`font-display font-bold text-4xl sm:text-5xl mb-8 ${
                      tier.highlight ? 'text-thunder' : 'text-electric'
                    }`}
                    style={tier.highlight ? { textShadow: '0 0 30px rgba(255,214,10,.3)' } : { textShadow: '0 0 30px rgba(0,180,216,.3)' }}
                  >
                    {tier.price}
                  </div>
                  <ul className="space-y-3 mb-10 flex-grow">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-center gap-3 text-white/70 text-sm">
                        <span className={tier.highlight ? 'text-thunder' : 'text-electric'}>&#x25B8;</span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
                    className={`w-full font-display font-bold py-4 tracking-[0.3em] text-sm transition ${
                      tier.highlight
                        ? 'bg-thunder hover:bg-thunder-dark text-charcoal-deep'
                        : 'bg-electric hover:bg-electric-bright text-charcoal-deep'
                    }`}
                  >
                    GET CUSTOM QUOTE →
                  </button>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Commercial Rates */}
      <section className="relative py-28 bg-charcoal overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <ScrollReveal className="text-center mb-16">
            <div className="text-thunder font-display tracking-[0.4em] text-xs mb-3">// COMMERCIAL</div>
            <h2 className="font-display font-bold text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
              COMMERCIAL <span className="text-electric">RATES</span>
            </h2>
            <p className="text-white/60 text-lg mt-6 max-w-xl mx-auto">
              Priced per square foot. Custom scope of work. Free walkthrough included.
            </p>
          </ScrollReveal>

          <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {COMMERCIAL_RATES.map((r, i) => (
              <ScrollReveal key={r.type} delay={i * 80}>
                <div className="bg-charcoal-deep border-l-4 border-electric hover:border-thunder p-8 transition-all hover:-translate-y-1">
                  <h3 className="font-display font-bold text-white text-xl mb-2 tracking-wider">{r.type}</h3>
                  <div className="font-display font-bold text-electric text-3xl mb-2" style={{ textShadow: '0 0 20px rgba(0,180,216,.3)' }}>
                    {r.rate}
                  </div>
                  <p className="text-white/50 text-sm">{r.note}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Fine Print + CTA */}
      <section className="relative py-28 bg-charcoal-deep overflow-hidden">
        <ScrollReveal className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="bg-charcoal-mid/40 border border-electric/20 p-10 mb-12">
            <div className="font-display text-thunder tracking-[0.3em] text-xs mb-4">// PRICING NOTES</div>
            <ul className="text-white/60 space-y-2 text-sm text-left max-w-xl mx-auto">
              <li>&#x25B8; Final pricing based on in-person walkthrough</li>
              <li>&#x25B8; Recurring service discounts available (weekly, bi-weekly)</li>
              <li>&#x25B8; No contracts required — cancel anytime</li>
              <li>&#x25B8; All supplies and equipment included</li>
              <li>&#x25B8; 100% satisfaction guarantee on every clean</li>
            </ul>
          </div>

          <h2 className="font-display font-bold text-white text-5xl sm:text-6xl leading-[0.9] mb-8">
            READY FOR YOUR <span className="text-thunder">CUSTOM QUOTE?</span>
          </h2>
          <div className="flex flex-col sm:flex-row gap-5 justify-center">
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
              className="pulse-btn bg-electric hover:bg-electric-bright text-charcoal-deep font-display font-bold px-12 py-5 text-sm tracking-[0.3em] transition glow-electric w-full sm:w-auto"
            >
              GET FREE QUOTE →
            </button>
            <a
              href="tel:4074616039"
              className="border-2 border-thunder text-thunder hover:bg-thunder hover:text-charcoal-deep font-display font-bold px-12 py-5 text-sm tracking-[0.3em] transition w-full sm:w-auto text-center"
            >
              CALL (407) 461-6039
            </a>
          </div>
        </ScrollReveal>
      </section>
    </div>
  );
}
